import { CACHE_VERSION, SNAPSHOT_STORE } from "./constants";
import type { CacheSnapshot } from "./CacheSnapshot";

const DB_NAME = "lumids-structured-cache";
const DB_VERSION = CACHE_VERSION;

const LEGACY_STORES = ["layouts", "channels", "rules", "metadata"];

export default class CacheDatabase {
    private static instance: CacheDatabase | null = null;
    private dbHandle: IDBDatabase | null = null;
    private opening: Promise<IDBDatabase> | null = null;

    static getInstance(): CacheDatabase {
        if (!CacheDatabase.instance) {
            CacheDatabase.instance = new CacheDatabase();
        }
        return CacheDatabase.instance;
    }

    private constructor() {}

    /* see docs/EXTRACTED_COMMENTS.md#c003 */
    public open(): Promise<IDBDatabase> {
        if (this.dbHandle) return Promise.resolve(this.dbHandle);
        if (this.opening) return this.opening;
        if (typeof indexedDB === "undefined") {
            return Promise.reject(new Error("IndexedDB not available"));
        }
        this.opening = this.attemptOpen()
            .then((db) => {
                this.dbHandle = db;
                this.opening = null;
                return db;
            })
            .catch((err) => {
                this.opening = null;
                throw err;
            });
        return this.opening;
    }

    private attemptOpen(): Promise<IDBDatabase> {
        return new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                // see docs/EXTRACTED_COMMENTS.md#c004
                for (const name of LEGACY_STORES) {
                    if (db.objectStoreNames.contains(name)) {
                        db.deleteObjectStore(name);
                    }
                }
                if (db.objectStoreNames.contains(SNAPSHOT_STORE)) {
                    db.deleteObjectStore(SNAPSHOT_STORE);
                }
                const store = db.createObjectStore(SNAPSHOT_STORE, { keyPath: "signature" });
                store.createIndex("by_layoutId", "layoutId", { unique: false });
                store.createIndex("by_hash", "hash", { unique: false });
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error ?? new Error("Failed to open cache database"));
            request.onblocked = () => reject(new Error("Cache database upgrade blocked by another tab"));
        });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c005 */
    private async withSnapshotStore<T>(
        mode: IDBTransactionMode,
        fn: (s: IDBObjectStore) => Promise<T> | T
    ): Promise<T> {
        const db = await this.open();
        return new Promise<T>((resolve, reject) => {
            const tx = db.transaction(SNAPSHOT_STORE, mode);
            const store = tx.objectStore(SNAPSHOT_STORE);
            let result: T;
            Promise.resolve(fn(store))
                .then((v) => { result = v; })
                .catch(reject);
            tx.oncomplete = () => resolve(result);
            tx.onerror = () => reject(tx.error ?? new Error("Transaction failed"));
            tx.onabort = () => reject(tx.error ?? new Error("Transaction aborted"));
        });
    }

    public async putSnapshot(entry: CacheSnapshot): Promise<void> {
        await this.withSnapshotStore("readwrite", (store) => {
            store.put(entry);
        });
    }

    public async getSnapshotBySignature(signature: string): Promise<CacheSnapshot | undefined> {
        return this.withSnapshotStore("readonly", (store) => {
            return new Promise<CacheSnapshot | undefined>((resolve, reject) => {
                const req = store.get(signature);
                req.onsuccess = () => resolve(req.result as CacheSnapshot | undefined);
                req.onerror = () => reject(req.error ?? new Error("Failed to read snapshot"));
            });
        });
    }

    public async getSnapshotsByLayoutId(layoutId: string): Promise<CacheSnapshot[]> {
        return this.withSnapshotStore("readonly", (store) => {
            return new Promise<CacheSnapshot[]>((resolve, reject) => {
                const index = store.index("by_layoutId");
                const req = index.getAll(layoutId);
                req.onsuccess = () => resolve((req.result ?? []) as CacheSnapshot[]);
                req.onerror = () => reject(req.error ?? new Error("Failed to read snapshots"));
            });
        });
    }

    public async getSnapshotsByHash(hash: string): Promise<CacheSnapshot[]> {
        return this.withSnapshotStore("readonly", (store) => {
            return new Promise<CacheSnapshot[]>((resolve, reject) => {
                const index = store.index("by_hash");
                const req = index.getAll(hash);
                req.onsuccess = () => resolve((req.result ?? []) as CacheSnapshot[]);
                req.onerror = () => reject(req.error ?? new Error("Failed to read snapshots"));
            });
        });
    }

    public async deleteSnapshot(signature: string): Promise<void> {
        await this.withSnapshotStore("readwrite", (store) => {
            store.delete(signature);
        });
    }

    public async wipeAll(): Promise<void> {
        await this.withSnapshotStore("readwrite", (store) => {
            store.clear();
        });
    }

    // Legacy alias retained for `killStructuredCache`.
    public async clearAll(): Promise<void> {
        await this.wipeAll();
    }
}
